// src/icon-system/runtime/ozellikKaydi.ts — ÜRÜN ÖZELLİĞİ → İKON AİLESİ KAYDI
//
// ══════════════════════════════════════════════════════════════════════════
// NİYE VAR (ölçülmüş boşluk, 2026-09-19)
// ══════════════════════════════════════════════════════════════════════════
// Depoda `contracts/uyumluluk.json` zaten var ve GÜÇLÜ: 52 ESKİ ikon adını
// semanticId'ye bağlar, `keyof typeof` ile bilinmeyen adı DERLEME hatası
// yapar (`adapters/cozumleyici.ts:19-22`). Ama o tablo GERİYE bakar — "şu
// Phosphor kısayolu hangi aileydi". İLERİYE bakan bir kayıt YOKTU: yarın
// eklenecek bir ÜRÜN ÖZELLİĞİ hangi aileyi kullanacak, erişilebilir adı ne,
// geçişli (seçili/seçili-değil) mi — bunlar her çağrı yerinde yeniden ve
// elle yazılıyordu. Ölçülen sonucu `BottomNav.tsx`te görünür:
//     varyant={activeTab === "home" ? "selected" : "outline"}
// aynı ifade FİVE kez, beş ayrı satırda (160, 172, 196, 229 + create'te hiç).
// `KULLANIM_SOZLESMESI.md §0.3` bu sınıfı zaten adlandırmış: *"yüzey yüzey
// dağılmış … satır-içi istisnalar"*.
//
// ── PROTOTİPTEN ALINAN FİKİR (experiments/living-icons, PR #1096) ─────────
// Prototipin `registry()`si üç şeyi ZORUNLU kılıyordu: açık kimlik, BİLİNEN
// bir intent (metinden TAHMİN YOK) ve erişilebilir etiket; çakışan semantik
// kimlik hata. Fikir doğru. Uygulaması bu depoya OLDUĞU GİBİ taşınmaz:
//   · Prototipin `families` sabiti 12 aileyi İKİNCİ bir yerde sayıyordu →
//     bu, motorun `IKON_ADLARI`sının yanında ikinci bir doğruluk kaynağı
//     olurdu (kanon: "kopyalanan kural çürür"). Burada aile adı TİPTİR
//     (`IkonAilesi` = `IkonAdi` = üretilmiş `IKON_ADLARI`) → bilinmeyen aile
//     DERLEME hatasıdır, çalışma zamanı kontrolü değil.
//   · Prototip aileyi `intent` adlı ayrı bir sözlükten çözüyordu; burada
//     ürün özelliği DOĞRUDAN aileyi bildirir. Araya bir eşleme koymak
//     `KULLANIM_SOZLESMESI §7`nin yasakladığı şeyi kolaylaştırırdı:
//     *"'Bu glif buna benziyor' eşlemesi yapma."*
//
// ── NE YAPMAZ (dürüst sınır) ─────────────────────────────────────────────
// · Metinden/etiketten aile TAHMİN ETMEZ. Etiket yalnız insan içindir.
// · İkinci bir ikon sistemi kurmaz: çizim, varyant, kontur, boyut, a11y ve
//   tembel yükleme kararlarının hepsi mevcut `IkonKoprusu`/`IconFrame`/
//   `motorYukleyici` hattında kalır. Bu dosya yalnız ÇAĞRI YERİNİN
//   tekrarladığı üç bilgiyi (aile · etiket · geçişli mi) tek yere toplar.
// · Kaydın aile adının üretilmiş motorda GERÇEKTEN var olduğunu ve TASLAK
//   olmadığını çalışma zamanında ölçmez — o, `generated/index.ts`i eager
//   pakete çekerdi (ölçülmüş bedel: `motorYukleyici.ts:4-11`). Bunu KAPI
//   ölçer: `src/__tests__/ikonOzellikKaydiSozlesmesi.test.tsx`.
// ══════════════════════════════════════════════════════════════════════════
import type { IkonAilesi } from "./IkonKoprusu";

/** Bir ürün özelliğinin ikon sözleşmesi. */
export interface OzellikKaydi {
  /** Ürün kimliği (`nav.home`). Küçük harf + nokta/tire; çakışma HATA. */
  readonly id: string;
  /** Motor ailesi — TİP olarak kısıtlı; bilinmeyen aile DERLEME hatasıdır. */
  readonly aile: IkonAilesi;
  /**
   * İnsan etiketi (erişilebilir ad adayı). ZORUNLU ve boş olamaz.
   * ⚠ Bu etiket ikona OTOMATİK basılmaz: çağrı yerinde düğmenin kendi
   * `aria-label`ı varsa ikon DEKORATİF kalmalıdır (çift okuma olmasın).
   * `OzellikIkonu` bunu `adTasiyici` propuyla açıkça sorar.
   */
  readonly etiket: string;
  /** Seçili/seçili-değil durumu olan özellik mi (nav sekmesi, kaydet…). */
  readonly gecisli: boolean;
  /**
   * Ailenin GÖRSEL ONAYI henüz alınmadı mı (motorda `taslak: true`).
   *
   * Üretim ≠ yayın: motor bir aileyi doğurabilir, doğrulayabilir ve
   * derleyebilir; ama kullanıcıya giden görünüm OWNER'ın görsel kabulünü
   * bekler (`CLAUDE.md` — owner-görünür kimlik varlıkları). Böyle bir kayıt
   * ürün ekranına GİREBİLİR, ama iki koşulla:
   *   1. derleyici o aileyi `IkonTaslak` ile üretir → ekranda görünür kesikli
   *      çerçeve taşır; onaylı bir ikon gibi DURMAZ,
   *   2. çağrı yeri ikon motoru bayrağının arkasındadır (bayrak kapalıyken
   *      eski görünüm birebir çizilir),
   *   3. (2026-09-25) VE yayın kapsamı `tam` olmalıdır: varsayılan kapsam
   *      `onayli`dır, orada taslak ailenin çağrı yeri motor AÇIKKEN de
   *      `eski`yi çizer (`ikonBayragi.ikonYayinKapsami` + `IkonKoprusu`).
   *      Onaylı aileler yayına açılırken taslaklar eski ikonda kalır.
   * Kapı bunu İKİ YÖNDE ölçer: taslak aile `onayBekliyor:false` olamaz,
   * onaylı aile `onayBekliyor:true` olamaz (bayat beyan yok).
   */
  readonly onayBekliyor: boolean;
  /**
   * ÜRETİLEN ADAY ailesi (varsa). "Aday önizleme" bayrağı AÇIKKEN `aile`
   * yerine BU aile çizilir.
   *
   * Niye var (owner, 2026-09-21): *"Üretilen adaylar gerçek ürün yüzeyinde
   * gösterilsin; mevcut insan çizimi REPOST bu kanıtın yerine geçmez."*
   * Aday ile insan çizimi aynı KAYITTA durur ama asla karışmaz:
   *   · bayraksız     → `aile` (bugünkü ürün davranışı, birebir)
   *   · `?ikonAday=on` → `adayAilesi` (üretilen aday; kendisi de `taslak:true`
   *                      olduğu için ekranda IkonTaslak kesikli çerçevesiyle)
   * ⚠ İnsan çizimi SİLİNMEZ, yeri DEĞİŞMEZ (owner koruma emri): aday AYRI bir
   *   ailedir, ayrı kaynak dosyalarıyla ve ayrı manifest girdisiyle.
   */
  readonly adayAilesi?: IkonAilesi;
}

export type OzellikGirdisi = Omit<OzellikKaydi, "gecisli" | "onayBekliyor"> & {
  gecisli?: boolean;
  onayBekliyor?: boolean;
};

const KIMLIK_DESENI = /^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/;

export interface OzellikDefteri<Kimlik extends string = string> {
  /** Kayıtlı özellik — bilinmeyen id HATA (sessiz `undefined` yok). */
  coz(id: string): OzellikKaydi;
  /** Kayıtlı id'ler (kapı ve önizleme okur). */
  kimlikler(): readonly Kimlik[];
}

/**
 * Kayıt defteri kurar. Doğrulama ÇAĞRI ANINDA yapılır (modül yüklenirken),
 * yani hatalı bir kayıt ilk render'da değil, dosya yüklenirken patlar.
 *
 * Reddedilen üç hâl (üçü de kapıda mutasyonla ölçülür):
 *   1. kimlik deseni tutmuyor  → HATA
 *   2. etiket boş/yalnız boşluk → HATA   (erişilebilir ad borcu sessiz kalmasın)
 *   3. aynı id, FARKLI içerikle ikinci kez → HATA (semantik kimlik çakışması)
 * Aynı id AYNI içerikle tekrar verilirse sorun değildir (idempotent).
 */
export function ozellikDefteriKur<Girdi extends OzellikGirdisi>(girdiler: readonly Girdi[]): OzellikDefteri<Girdi["id"]> {
  if (!Array.isArray(girdiler)) throw new Error("[ikon] özellik girdileri dizi olmalı.");
  const kayitlar = new Map<string, OzellikKaydi>();
  const kimlikler = new Set<Girdi["id"]>();
  for (const g of girdiler) {
    if (!g || typeof g !== "object") throw new Error("[ikon] özellik kaydı nesne olmalı.");
    if (typeof g.id !== "string" || !KIMLIK_DESENI.test(g.id)) {
      throw new Error(`[ikon] özellik kimliği geçersiz: ${JSON.stringify(g.id)} (desen: ${KIMLIK_DESENI})`);
    }
    if (typeof g.etiket !== "string" || g.etiket.trim() === "") {
      throw new Error(`[ikon] "${g.id}" için etiket ZORUNLU (erişilebilir ad adayı; boş olamaz).`);
    }
    // JS/JSON çağıranı da sessiz varsayılana düşemez. Ailenin katalogda
    // varlığı derleme aracında ölçülür; burada generated chunk yüklenmez.
    if (typeof g.aile !== "string" || !g.aile.trim()) {
      throw new Error(`[ikon] "${g.id}": aile zorunlu.`);
    }
    if ((g.gecisli !== undefined && typeof g.gecisli !== "boolean") ||
        (g.onayBekliyor !== undefined && typeof g.onayBekliyor !== "boolean")) {
      throw new Error(`[ikon] "${g.id}": durum alanları boolean olmalı.`);
    }
    if (g.adayAilesi !== undefined && (typeof g.adayAilesi !== "string" || !g.adayAilesi.trim())) {
      throw new Error(`[ikon] "${g.id}": adayAilesi boş olamaz.`);
    }
    const kayit: OzellikKaydi = Object.freeze({
      id: g.id,
      aile: g.aile,
      etiket: g.etiket,
      gecisli: g.gecisli === true,
      onayBekliyor: g.onayBekliyor === true,
      ...(g.adayAilesi ? { adayAilesi: g.adayAilesi } : {}),
    });
    if (g.adayAilesi && g.adayAilesi === g.aile) {
      throw new Error(`[ikon] "${g.id}": adayAilesi, ailenin KENDİSİ olamaz (${g.aile}) — aday ayrı bir ailedir.`);
    }
    const onceki = kayitlar.get(g.id);
    if (
      onceki &&
      (onceki.aile !== kayit.aile ||
        onceki.etiket !== kayit.etiket ||
        onceki.gecisli !== kayit.gecisli ||
        onceki.onayBekliyor !== kayit.onayBekliyor ||
        onceki.adayAilesi !== kayit.adayAilesi)
    ) {
      throw new Error(
        `[ikon] semantik kimlik çakışması: "${g.id}" zaten ${onceki.aile}/${JSON.stringify(onceki.etiket)} ` +
          `olarak kayıtlı, şimdi ${kayit.aile}/${JSON.stringify(kayit.etiket)} isteniyor.`,
      );
    }
    kayitlar.set(g.id, kayit);
    kimlikler.add(g.id);
  }
  return {
    coz(id: string): OzellikKaydi {
      const k = kayitlar.get(id);
      if (!k) throw new Error(`[ikon] kayıtsız özellik: ${JSON.stringify(id)} (önce ozellikDefteriKur ile kaydet).`);
      return k;
    },
    kimlikler(): readonly Girdi["id"][] {
      return [...kimlikler];
    },
  };
}

/**
 * ÜRÜN DEFTERİ — gerçek ekranlarda kullanılan özellikler.
 *
 * Alt gezinme (`src/BottomNav.tsx`), MASAÜSTÜ yan gezinme (`src/SideNav.tsx`)
 * ve MyClips V3 etkileşim rayı (`src/myclipsv3/engagement/EngagementRailV3.tsx`)
 * bu defterden okur. Yeni bir özellik eklemek = buraya BİR satır: aile TİPLE
 * kısıtlıdır (yanlış yazım derlemede düşer), etiket zorunludur, geçişlilik ve
 * onay durumu tek yerde yazılıdır.
 *
 * ── MASAÜSTÜ NİYE SONRADAN GELDİ (ölçülmüş boşluk, 2026-09-20) ───────────
 * Kayıt ilk turda yalnız `BottomNav`a bağlanmıştı; `SideNav` doğrudan
 * `IkonKoprusu` çağırıyordu. Sonucu ölçüm aletinde görünüyordu: hareket
 * ekseni düğümü KAYIT KİMLİĞİNDEN (`[data-ozellik]`) bulur, masaüstü
 * yüzeyinde hiçbir düğüm o kimliği taşımıyordu → eksen "DOM'da
 * `data-ozellik` taşıyan ikon YOK" diyip ÖLÇEMEDİM dönüyordu. Yani
 * masaüstünde zincir kopuk değildi, GÖRÜNMEZDİ — ve görünmeyen zincir
 * korunmayan zincirdir.
 */
export const URUN_OZELLIKLERI = ozellikDefteriKur([
  // ── alt gezinme (mobil) ────────────────────────────────────────────────
  { id: "nav.home", aile: "navigation.home", etiket: "Ana Sayfa", gecisli: true },
  { id: "nav.explore", aile: "navigation.search", etiket: "Keşfet", gecisli: true },
  // ⚠ `gecisli: false` İDİ — o beyan YALNIZ `BottomNav`a bakılarak yazılmıştı.
  //    Masaüstü `SideNav` bağlanınca ÖLÇÜM değişti: orada aktif "Oluştur"
  //    satırı `varyant="selected"` çiziyor (ikonDalga1Sozlesmesi (iv) bunu
  //    ölçer). Aile `statePolicy: "esit"` olduğu için iki glif bayt-eşittir,
  //    yani piksel farkı YOK; ama beyanın ürünle uyuşması gerekir — bayat
  //    beyan, daha dar bir yüzeyden genelleme yapmanın izidir.
  { id: "nav.create", aile: "navigation.create", etiket: "Oluştur", gecisli: true },
  { id: "nav.clips", aile: "navigation.clips", etiket: "MyClips", gecisli: true },
  { id: "nav.discovery", aile: "navigation.discovery", etiket: "Sosyal Keşif", gecisli: true },
  // ── yan gezinme (masaüstü) — AYNI defter, ikinci bir yol YOK ───────────
  { id: "nav.map", aile: "navigation.map", etiket: "Konumlar", gecisli: true },
  { id: "nav.messages", aile: "messaging.inbox", etiket: "Mesajlar", gecisli: true },
  { id: "nav.notifications", aile: "notification.bell", etiket: "Bildirimler", gecisli: true },
  { id: "nav.discoveries", aile: "navigation.discoveries", etiket: "İlk Işıklar", gecisli: true },
  { id: "nav.more", aile: "action.more", etiket: "Daha fazla", gecisli: false },
  // ── MyClips V3 etkileşim rayı ─────────────────────────────────────────
  // ⚠ ONAY BEKLİYOR: `content.repost` motorda `taslak: true` → `IkonTaslak`
  // (kesikli çerçeve). Ürün gösterimi ikon motoru bayrağının ARKASINDADIR;
  // bayrak kapalıyken bugünkü görünüm birebir çizilir.
  // ⚠ VE İDDİA DAR: bu aile "otomatik üretilmedi". Motor zinciri uçtan uca
  //   koştu (doğum · ebeveyn · aday arama · eleme · ayrışma hükmü), ama en iyi
  //   aday motorun KENDİ ölçeriyle elendi (komşusuna IoU 0.8293) ve motor
  //   `karar: yok` dedi; GEOMETRİYİ insan çizdi. Adım-adım ayrım:
  //   `docs/ikon/YASAYAN_MOTOR.md` §7b.0.
  // `adayAilesi` (2026-09-21): ÜRETİLEN aday `content.repostAdayi` — geometrisine
  // insan eli DEĞMEDİ (`yapi.js` "cevrim" sınıfı, anlamdan seçildi; iki yay + iki
  // TEĞETSEL ok ucu → dönüş yönü). Yalnız `?ikonAday=on` ile çizilir; varsayılanda
  // insan çizimi birebir korunur (owner koruma emri).
  { id: "clip.repost", aile: "content.repost", etiket: "Feed'e ekle", gecisli: true, onayBekliyor: true, adayAilesi: "content.repostAdayi" },
  // ── profil · yıldız yolculuğu çipi ────────────────────────────────────
  // ÖLÇÜLMÜŞ BOŞLUK (2026-09-20): `ProfileHeaderMobile.tsx` "Star yolculuğu"
  // çipini `navigation.discovery` (anlamı *Sosyal Keşif*) ile çiziyordu — bir
  // ailenin glifi BAŞKA bir fiil için kullanılıyordu. `reputation.starJourney`
  // bu fiilin KENDİ ailesidir ve ÜRETİCİDEN doğdu (`yapi.js` "yonluBaglanti":
  // ray + kademeli düğümler + fileto tepeli ok ucu); insan eli geometriye
  // DEĞMEDİ. `taslak: true` → ekranda IkonTaslak kesikli çerçevesi.
  // ── profil menüsü · anlamı doğrulanmış sekiz eylem ───────────────────
  // Arşiv, engellenenler, gizli kelimeler, dışa aktarma ve öneriler için
  // motor ailesi eklenmedi; görsel benzerlik üzerinden eşleme yapılmaz.
  { id: "profile.menu.settings", aile: "system.settings", etiket: "Ayarlar", gecisli: false },
  { id: "profile.menu.creator-dashboard", aile: "stats.chart", etiket: "Klip Analitiğim", gecisli: false },
  { id: "profile.menu.close-friends", aile: "social.people", etiket: "Yakın Arkadaşlar", gecisli: false },
  { id: "profile.menu.push-preferences", aile: "notification.bell", etiket: "Bildirim Ayarları", gecisli: false },
  { id: "profile.menu.qr", aile: "profile.qr", etiket: "QR Kodu", gecisli: false },
  { id: "profile.menu.saved", aile: "content.save", etiket: "Kaydedilenler", gecisli: false },
  { id: "profile.menu.share", aile: "content.shareExternal", etiket: "Profili paylaş", gecisli: false },
  { id: "profile.menu.delete-account", aile: "action.delete", etiket: "Hesabımı Sil", gecisli: false },
  { id: "profile.star-journey", aile: "reputation.starJourney", etiket: "Yıldız yolculuğu", gecisli: false, onayBekliyor: true },
] as const);

/** Ürün kimliği tek katalogdan türer; ikinci bir string listesi yoktur. */
export type FeatureId = ReturnType<typeof URUN_OZELLIKLERI.kimlikler>[number];
